/**
 * Staff preview mode: "am I an operator looking at this portal as the customer
 * would see it?"
 *
 * The CRM's "View project as customer" mints a short-lived link into this app
 * (`/?preview=<token>&tab=projects&project=<id>`). While that token is in the
 * address bar, every request this app makes carries it, and the API answers with
 * the customer's records instead of the signed-in user's own - which for an
 * operator would otherwise be nothing at all.
 *
 * ── THE URL IS THE ONLY SOURCE ─────────────────────────────────────────────
 *
 * Not `sessionStorage`, and the reason is worth writing down because caching it
 * looks like the tidier build. A stored token outlives the visit: the operator
 * previews a client, then in the same tab opens the portal normally, and every
 * request still carries a token that has since expired - so their own portal
 * answers 403 instead of rendering. Reading the address bar every time cannot
 * drift, and it has a second benefit the cache does not: the operator can SEE
 * which mode they are in.
 *
 * The cost is that every navigation inside the app has to keep the parameter.
 * `keepPreview` below is what does that, and every `setParams` call in this app
 * goes through it.
 *
 * ── IT AUTHORISES NOTHING ──────────────────────────────────────────────────
 *
 * The token names a client. The API refuses it unless the SAME request also
 * carries a signed-in operator holding `view:crm`, which the browser has because
 * the session cookie covers both hosts. So a token in the URL is a request to
 * widen, never a credential - and a link that leaks is worth nothing to anybody
 * who is not already staff.
 */

/** The parameter the CRM writes and the API reads. Mirrors
 *  `apps/api/src/lib/portal-preview.ts`. */
export const PREVIEW_PARAM = "preview";

/** The header the API prefers, because a header cannot be pasted or bookmarked
 *  by accident. Mirrors `PORTAL_PREVIEW_HEADER` on the API side. */
export const PREVIEW_HEADER = "X-Portal-Preview";

/** Which project the link was minted from, so the projects tab can open it. */
export const PREVIEW_PROJECT_PARAM = "project";

/**
 * The token in the address bar right now, or null.
 *
 * Shape-checked here as well as on the server. Not defence - the server's check
 * is the one that counts - but so that a mistyped or truncated link produces the
 * ordinary portal rather than a round trip that comes back 403 and reads as an
 * outage.
 */
export function previewToken(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(PREVIEW_PARAM)?.trim() ?? "";
  return /^[0-9a-f]{64}$/.test(raw) ? raw : null;
}

/** Is this window a staff preview? */
export function isPreview(): boolean {
  return previewToken() !== null;
}

/**
 * Carry the preview parameters through a navigation that rewrites the query.
 *
 * `setParams({ tab })` REPLACES the whole query string, which is right for an
 * ordinary visitor and fatal here: the first tab click would drop the token and
 * the portal would empty itself mid-preview, looking exactly like a client with
 * no records. Wrapping the object in this keeps the token and nothing else, so a
 * normal visit is unchanged.
 */
export function keepPreview(next: Record<string, string>): Record<string, string> {
  const token = previewToken();
  if (!token) return next;
  // The TOKEN is carried and the preview's project id deliberately is not.
  // Which project is open is now ordinary URL state that the projects page reads
  // and writes for itself (`?project=`), so the only thing this function still
  // has to decide is what a TOP-LEVEL tab click means - and it means "leave the
  // project", the same as it does for any visitor. Carrying the landing
  // instruction across a tab switch would reopen a project the operator had just
  // closed, over the list they were trying to look at.
  return { ...next, [PREVIEW_PARAM]: token };
}

/**
 * Append the token to a plain link.
 *
 * For the one door a header cannot reach: a PDF is opened as an `<a href>`, so
 * without this every document in a preview would answer 403 while the pages
 * around it worked - which reads as a broken portal rather than as a boundary.
 */
export function withPreview(url: string): string {
  const token = previewToken();
  if (!token) return url;
  return `${url}${url.includes("?") ? "&" : "?"}${PREVIEW_PARAM}=${encodeURIComponent(token)}`;
}
